import { useState, useEffect, useCallback, useRef } from "react";
import { AdminContainer } from "../components/layouts";
import api from "../api";
import {
  Button,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from "@material-ui/core";
import { Search as SearchIcon, Clear as ClearIcon } from "@material-ui/icons";
import { useHistory, useLocation } from "react-router-dom";
import AggregatedPaymentPreviewModal from "../components/AggregatedPaymentPreviewModal";
import StatementsTable from "../components/StatementsTable";
import { toast } from "react-toastify";

export default function AggregatePaymentsPage() {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialPartnerId = queryParams.get("partnerId");
  const initialPartnerName = queryParams.get("partnerName");

  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(
    initialPartnerId
      ? { id: Number(initialPartnerId), name: initialPartnerName || `Partner ${initialPartnerId}` }
      : null
  );
  const [selectedPartnerId, setSelectedPartnerId] = useState(
    initialPartnerId ? Number(initialPartnerId) : ""
  );
  const [partnerSearch, setPartnerSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [statements, setStatements] = useState([]);
  const [selectedStatementIds, setSelectedStatementIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [startedAfter, setStartedAfter] = useState("");
  const [startedBefore, setStartedBefore] = useState("");
  const [billingPeriod, setBillingPeriod] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState("");
  // Client-generated idempotency key to prevent duplicate ACH transfers on double-submit
  const idempotencyKeyRef = useRef(
    crypto.randomUUID()
  );

  const handlePartnerSearch = () => {
    if (partnerSearch.length < 1) return;
    setSearchLoading(true);
    setPartners([]);
    api
      .getPartners({ search: partnerSearch, perPage: 20 })
      .then((resp) => {
        const results = resp.data?.items || resp.data || [];
        setPartners(results);
        // Auto-select if exactly one result
        if (results.length === 1) {
          selectPartner(results[0]);
        }
      })
      .catch(() => toast.error("Failed to search partners"))
      .finally(() => setSearchLoading(false));
  };

  const selectPartner = (partner) => {
    setSelectedPartner(partner);
    setSelectedPartnerId(Number(partner.id));
    setStatements([]);
    setSelectedStatementIds([]);
    setPartners([]);
  };

  const clearPartner = () => {
    setSelectedPartner(null);
    setSelectedPartnerId("");
    setPartnerSearch("");
    setPartners([]);
    setStatements([]);
    setSelectedStatementIds([]);
    setBankAccounts([]);
    setSelectedBankAccountId("");
  };

  // Load bank accounts when partner selected
  useEffect(() => {
    if (!selectedPartnerId) {
      setBankAccounts([]);
      setSelectedBankAccountId("");
      return;
    }
    api
      .getPartnerPaymentInstruments({ id: selectedPartnerId })
      .then((resp) => {
        const items = resp.data?.items || resp.data || [];
        setBankAccounts(items);
      })
      .catch(() => setBankAccounts([]));
  }, [selectedPartnerId]);

  // Load statements when partner selected
  const loadStatements = useCallback(() => {
    if (!selectedPartnerId) return;
    setStatementsLoading(true);
    setError(null);
    const params = { partnerId: selectedPartnerId };
    if (startedAfter) params.startedAfter = startedAfter;
    if (startedBefore) params.startedBefore = startedBefore;
    if (billingPeriod) params.billingPeriod = billingPeriod;
    if (selectedBankAccountId) params.bankAccountId = selectedBankAccountId;

    api
      .getStatementsForAggregation(params)
      .then((resp) => {
        setStatements(resp.data || []);
        setSelectedStatementIds([]);
      })
      .catch((err) => setError(err.message || "Failed to load statements"))
      .finally(() => setStatementsLoading(false));
  }, [selectedPartnerId, startedAfter, startedBefore, billingPeriod, selectedBankAccountId]);

  useEffect(() => {
    loadStatements();
  }, [loadStatements]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStatementIds(statements.map((s) => s.id));
    } else {
      setSelectedStatementIds([]);
    }
  };

  const handleSelectStatement = (id) => {
    setSelectedStatementIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handlePreview = () => {
    if (!selectedStatementIds.length) return;
    setLoading(true);
    setError(null);
    api
      .previewAggregatedPayments({
        partnerId: selectedPartnerId,
        statementIds: selectedStatementIds
      })
      .then((resp) => {
        setPreviewData(resp.data);
        setPreviewOpen(true);
      })
      .catch((err) => setError(err.response?.data?.message || "Preview failed"))
      .finally(() => setLoading(false));
  };

  const handleCreate = () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    api
      .createAggregatedPayments({
        partnerId: selectedPartnerId,
        statementIds: selectedStatementIds,
        idempotencyKey: idempotencyKeyRef.current
      })
      .then(() => {
        setPreviewOpen(false);
        history.push("/aggregated-payments");
      })
      .catch((err) => setError(err.response?.data?.message || "Creation failed"))
      .finally(() => setCreating(false));
  };


  return (
    <AdminContainer variant="list">
      <Typography variant="h5" gutterBottom>
        Create Aggregated Payments
      </Typography>

      {error && (
        <Box mb={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Step 1: Select Partner */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          1. Select Partner
        </Typography>
        {selectedPartner ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip
              label={`${selectedPartner.name} (ID: ${selectedPartner.id})`}
              color="primary"
              onDelete={clearPartner}
            />
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <TextField
                label="Partner name or ID"
                variant="outlined"
                size="small"
                value={partnerSearch}
                onChange={(e) => setPartnerSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handlePartnerSearch();
                }}
                style={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: partnerSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setPartnerSearch("");
                          setPartners([]);
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handlePartnerSearch}
                disabled={searchLoading || partnerSearch.length < 1}
              >
                {searchLoading ? <CircularProgress size={20} /> : "Search"}
              </Button>
            </Box>
            {partners.length > 0 && (
              <Paper variant="outlined" style={{ maxHeight: 200, overflow: "auto", marginTop: 8, maxWidth: 500 }}>
                <List dense>
                  {partners.map((p) => (
                    <ListItem
                      key={p.id}
                      button
                      onClick={() => selectPartner(p)}
                    >
                      <ListItemText primary={`${p.name} (ID: ${p.id})`} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Step 2: Filters */}
      {selectedPartnerId && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            2. Filters (Optional)
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <TextField
              label="Started After"
              type="date"
              variant="outlined"
              size="small"
              value={startedAfter}
              onChange={(e) => setStartedAfter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Started Before"
              type="date"
              variant="outlined"
              size="small"
              value={startedBefore}
              onChange={(e) => setStartedBefore(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Billing Period</InputLabel>
              <Select
                value={billingPeriod}
                onChange={(e) => setBillingPeriod(e.target.value)}
                label="Billing Period"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
              <InputLabel>Bank Account</InputLabel>
              <Select
                value={selectedBankAccountId}
                onChange={(e) => setSelectedBankAccountId(e.target.value)}
                label="Bank Account"
              >
                <MenuItem value="">All</MenuItem>
                {bankAccounts.map((ba) => (
                  <MenuItem key={ba.id} value={ba.id}>
                    {ba.label || ba.name || "Account"}{" "}
                    {ba.last4 ? `(****${ba.last4})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={loadStatements}>
              Refresh
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 3: Select Statements */}
      {selectedPartnerId && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            3. Select Statements ({selectedStatementIds.length} of{" "}
            {statements.length} selected)
          </Typography>

          {statementsLoading && <CircularProgress />}
          {!statementsLoading && statements.length === 0 && (
            <Typography color="textSecondary">
              No pending statements available for aggregation.
            </Typography>
          )}
          {!statementsLoading && statements.length > 0 && (
            <StatementsTable
              statements={statements}
              selectable
              selectedIds={selectedStatementIds}
              onSelect={handleSelectStatement}
              onSelectAll={handleSelectAll}
            />
          )}
        </Box>
      )}

      {/* Actions */}
      {selectedStatementIds.length > 0 && (
        <Box display="flex" style={{ gap: 16 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePreview}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Preview & Create"}
          </Button>
          <Button variant="outlined" onClick={() => setSelectedStatementIds([])}>
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Preview Modal */}
      <AggregatedPaymentPreviewModal
        open={previewOpen}
        groups={previewData}
        onClose={() => setPreviewOpen(false)}
        onCreate={handleCreate}
        loading={creating}
      />
    </AdminContainer>
  );
}
